import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Col, Row, Collapse } from 'reactstrap';
import { updateUi } from '../../redux';
import moment from 'moment';
import { Utils } from '../../services';
import i18n from '../../locales/i18n';
import config from '../../config/Config';
import { fetchYodaChangelog, presentChange } from '../../services/yodaChangelog';

import './ChangeLog.css';
import ModalComponent from '../ModalComponent';

/** Same count as the old client-side slice(0, 10); also sent as ?limit= when the API supports it. */
const PANEL_RELEASE_LIMIT = 10;
const CHANGELOG_TIMEOUT_MS = 20000;

const buildReleaseEntry = (releaseNo, releaseDate, changes) => {
    const releaseChanges = {
        feature: [],
        fix: [],
        ui: [],
        i18n: [],
        build: []
    };
    (changes || []).forEach((change) => {
        const presented = presentChange(change);
        if (releaseChanges[presented.type])
            releaseChanges[presented.type].push(presented);
    });
    const hasVisible = Object.values(releaseChanges).some((list) => list.length > 0);
    if (!hasVisible)
        return null;
    return { releaseNo, releaseDate, releaseChanges };
};

const ChangeLog = () => {
    const dispatch                          = useDispatch();
    const { t }                             = useTranslation();
    const { showChangelog }                 = useSelector(state => state.ui);
    const [changelog, setChangeLog]         = useState(null);
    const [upcoming, setUpcoming]           = useState([]);
    const [opened, setOpened]               = useState(0);
    const [firstShowFlag, setFirstShowFlag] = useState(false);
    const [changelogUrl, setChangelogUrl]   = useState(null);
    const [isOpen, setIsOpen]               = useState(false);
    const [imageUrl, setImageUrl]           = useState(null);
    const [message, setMessage]             = useState(null);
    const [loading, setLoading]             = useState(false);
    const [loadError, setLoadError]         = useState(false);

    const isDevBuild = !config.app_version;

    const releaseTypeValues = {
        feature: {
            icon: <i className='fa fa-plus'></i>,
            text: t('changelog.features'),
            color: 'success'
        },
        fix: {
            icon: <i className='fa fa-bug'></i>,
            text: t('changelog.fixes'),
            color: 'warning'
        },
        i18n: {
            icon: <i className='fa fa-language'></i>,
            text: t('changelog.i18n'),
            color: 'primary'
        },
        ui: {
            icon: <i className='fa fa-paint-brush'></i>,
            text: t('changelog.ui'),
            color: 'info'
        },
        build: {
            icon: <i className='fa fa-cog'></i>,
            text: t('changelog.builds'),
            color: 'muted'
        },
    };

    const getContent = useCallback(async () => {
        setLoadError(false);
        setLoading(true);
        try {
            const result = await fetchYodaChangelog({
                projectId: config.yoda_project_id,
                mergeIds: [config.yoda_kore_project_id],
                limit: PANEL_RELEASE_LIMIT,
                timeoutMs: CHANGELOG_TIMEOUT_MS,
            });
            const releases = result.changelog || [];
            // Newest first; client cap until yoda-server ?limit= / SDK limit are deployed.
            setChangeLog(releases.slice().reverse().slice(0, PANEL_RELEASE_LIMIT));
            // Upcoming only in local/dev builds without a release version.
            setUpcoming(isDevBuild && Array.isArray(result.upcoming) ? result.upcoming : []);
            setChangelogUrl(result.changelogUrl || null);
        } catch (e) {
            console.log(e);
            setChangeLog(null);
            setUpcoming([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [isDevBuild]);

    useEffect(() => {
        if (showChangelog)
            setFirstShowFlag(true);
    }, [showChangelog]);

    useEffect(() => {
        if (showChangelog)
            getContent();
    }, [showChangelog, getContent]);

    const processedChangelog = useMemo(() => {
        const tempResult = [];

        if (upcoming?.length > 0 && isDevBuild) {
            const entry = buildReleaseEntry(
                t('changelog.upcoming'),
                t('changelog.upcoming_hint'),
                upcoming
            );
            if (entry)
                tempResult.push(entry);
        }

        if (changelog?.length > 0) {
            for (let i = 0; i < changelog.length; i++) {
                const release = changelog[i];
                const dateSource = release.date || release.commit?.date;
                const entry = buildReleaseEntry(
                    release.version,
                    dateSource ? moment(dateSource).format('ll') : '',
                    release.changes
                );
                if (entry)
                    tempResult.push(entry);
            }
        }

        return tempResult;
    }, [changelog, upcoming, isDevBuild, t]);

    const hideChangeLog = () => {
        dispatch(updateUi({ showChangelog: !showChangelog }));
    };

    const toggleCollapse = (index) => {
        setOpened(index);
    }
    const goToChangelog = () => {
        window.open(changelogUrl, '_blank');
    };

    const panelBody = () => {
        if (loading)
            return <i className="fa fa-spinner fa-spin fa-3x fa-fw"></i>;

        if (loadError)
            return (
                <div className="changelog-error text-center mt-4">
                    <p>{t('changelog.load_error')}</p>
                    <Button color="info" onClick={getContent}>
                        <i className="fa fa-refresh"></i> {t('changelog.retry')}
                    </Button>
                </div>
            );

        if (processedChangelog.length === 0)
            return <p className="text-center mt-4">{t('changelog.empty')}</p>;

        return (
            <>
            {processedChangelog.map((c, i) =>
                <div key={i}>
                    <Button className="hover-version w-100 bg-transparent border-0 rounded-0 mb-1 text-left text-dark" onClick={() => toggleCollapse(i)}>
                        <h5>
                            <b>
                                <i className='fa fa-rocket'></i>&nbsp;{c.releaseNo}
                                <span className='d-block float-right'>{c.releaseDate}</span>
                            </b>
                        </h5>
                    </Button>
                    <Collapse isOpen={opened === i}>
                        {Object.keys(c.releaseChanges).filter(key => c.releaseChanges[key].length > 0).map((key, index) =>
                            <Row className='px-2 mb-4' key={(i + index).toString()}>
                                <Col xs='12' className='pt-2 mb-2 mt-2 d-flex align-items-center'>
                                    {releaseTypeValues[key]?.icon}&nbsp;
                                    <Badge color={releaseTypeValues[key]?.color}>{releaseTypeValues[key]?.text}</Badge>
                                </Col>
                                {c.releaseChanges[key].map((change, id) =>
                                    <React.Fragment key={i + key + id}>
                                        <Col xs="1" className='mt-2'><small><i className='fa fa-bolt'></i></small></Col>
                                        <Col xs="11" className='mt-2'><b>{Utils.localized_or_fallback(change.title, i18n.language)}</b></Col>
                                        <Col xs="11" className="offset-1" dangerouslySetInnerHTML={{ __html: Utils.localized_or_fallback(change.description, i18n.language) }} />
                                        {change.imageUrl &&
                                            <Col xs="11" className='offset-1 mt-2 mb-2'>
                                                <img
                                                    className='mw-100'
                                                    src={change.imageUrl}
                                                    onClick={() => {
                                                        setMessage(Utils.localized_or_fallback(change.title, i18n.language));
                                                        setImageUrl(change.imageUrl);
                                                        setIsOpen(true);
                                                    }}
                                                    alt={change.title}
                                                />
                                            </Col>
                                        }
                                    </React.Fragment>
                                )}
                            </Row>
                        )}
                    </Collapse>
                </div>
            )}
            { changelogUrl &&
                <Button className='w-100 text-center mt-3' color="info" onClick={goToChangelog}>
                    <i className='fa fa-history'></i> {t('app.history') }
                </Button>
            }
            {isOpen && imageUrl &&
                <ModalComponent onClose={() => setIsOpen(false)}>
                    <div className="changelog-modal text-center mt-3">
                        <h3 className="title">{message}</h3>
                            <img src={imageUrl} className="w-100 img" alt={message || t('app.history')} />
                        </div>
                </ModalComponent>
            }
            </>
        );
    };

    return (
        <div className={`Changelog${showChangelog ? " expandedLog" : " reducedLog"}${firstShowFlag ? " d-block" : ""}`}>
            {(showChangelog) &&
                <>
                    <Button color="danger" size="sm" className='float-right' onClick={hideChangeLog}>
                        <i className='fa fa-times'></i>
                    </Button>
                    <h3 className='border-bottom mt-1'>
                        <i className='fa fa-code-fork'></i>&nbsp;{t('changelog.changelog')}
                    </h3>
                    {panelBody()}
                </>
            }
        </div>
    );
}

export default ChangeLog;
